import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { formatText } from "../i18n/texts";
import { portalHref } from "../shell/routes";
import { ChatIcon } from "../ui/icons/Icon";
import { DraftGuard } from "./DraftGuard";
import type { ConfiguredRole } from "./teamApi";
import { replaceUrl, useApi, useOperation } from "./teamHooks";
import { count, groupSummary, members as membersText } from "./teamText";
import {
  More,
  TeamEmpty,
  TeamSwitch,
  TeamWindow,
  useFeedback,
  useT,
  useTeam as useTeamSafe,
  type WindowHandle,
} from "./TeamUi";

// Etapa C · el detalle de una función de grupo (OperatingRolesWorkspace.razor): Alcance · Acciones · Menús sin modo de
// edición; lo que se toca queda en el borrador y se guarda con «Guardar cambios», con «Afecta a N miembros» y, si el cambio
// alcanza a miembros, la confirmación C12 con la lista de cambios. «Ver» (?ver=1) es de solo lectura; «Duplicar»
// (?duplicar=1) abre una función nueva con los mismos permisos.
//
// Lo usa Roles y permisos de Chat › Configuración (OperatingChatRolesWorkspace, del área de Configuración) para el detalle de
// una función de grupo: se dibuja dentro de su TeamContext/FeedbackScope/MenuScope.

const readGroup = "chat.read.group";
const actionCodes = ["chat.take", "chat.assign", "chat.reply", "chat.attach", "chat.close"];
const permissionKey: Record<string, string> = {
  [readGroup]: "CFG1_GROUP_READ",
  "chat.take": "CFG1_TAKE",
  "chat.assign": "CFG1_REASSIGN",
  "chat.reply": "CFG1_REPLY",
  "chat.attach": "CFG1_ATTACH",
  "chat.close": "CFG1_CLOSE",
};
const helpKey: Record<string, string> = {
  [readGroup]: "CFG1_R1_GROUP_READ_HELP",
  "chat.take": "CFG1_R1_TAKE_HELP",
  "chat.assign": "CFG1_R1_ASSIGN_HELP",
  "chat.reply": "CFG1_R1_REPLY_HELP",
  "chat.attach": "CFG1_R1_ATTACH_HELP",
};
const canEdit = (role: ConfiguredRole) => !role.protected && !role.actorUsesRole;
const sameSet = (a: Set<string>, b: string[]) => a.size === new Set(b).size && b.every((code) => a.has(code));

export function GroupRoleEditor({ roleId, duplicate = false }: { roleId: number; duplicate?: boolean }) {
  const T = useT();
  const ui = useTeamSafe();
  const api = useApi();
  const navigate = useNavigate();
  const feedback = useFeedback();
  const { busy, error, run, accept } = useOperation();
  const [setup] = useState(() => {
    const query = new URLSearchParams(location.search);
    const keep = ["q", "p"]
      .filter((key) => query.has(key))
      .map((key) => `${key}=${encodeURIComponent(query.get(key) ?? "")}`);
    return { viewOnly: query.has("ver"), listQuery: keep.length === 0 ? "" : "?" + keep.join("&") };
  });
  const [viewOnly, setViewOnly] = useState(setup.viewOnly);
  const [roles, setRoles] = useState<ConfiguredRole[]>([]);
  const [current, setCurrent] = useState<ConfiguredRole | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [section, setSection] = useState("actions");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [clone, setClone] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [createdId, setCreatedId] = useState(0);
  const confirmWindow = useRef<WindowHandle>(null);
  const currentId = createdId > 0 ? createdId : roleId;
  const listHref = "/portal/chat/configuracion/roles" + setup.listQuery;
  const editHref = `/portal/chat/configuracion/roles/${currentId}${setup.listQuery}`;
  const readOnly = viewOnly || current === null || (current.roleId > 0 && !canEdit(current));

  const roleName = (role: ConfiguredRole) =>
    role.code === "CFG1_CHAT_OPERATOR"
      ? T("CFG1_OPERATOR")
      : role.code === "CFG1_CHAT_READER"
        ? T("CFG1_READER")
        : role.code === "CFG1_CHAT_SUPERVISOR"
          ? T("CFG1_SUPERVISOR")
          : role.name;
  const roleDescription = (role: ConfiguredRole) =>
    role.code === "CFG1_CHAT_OPERATOR"
      ? T("CFG1_R1_OPERATOR_DESC")
      : role.code === "CFG1_CHAT_READER"
        ? T("CFG1_R1_READER_DESC")
        : role.code === "CFG1_CHAT_SUPERVISOR"
          ? T("CFG1_R1_SUPERVISOR_DESC")
          : role.description?.trim()
            ? role.description
            : groupSummary(ui, role.permissions);
  const permissionName = (code: string) => T(permissionKey[code] ?? "CFG1_ACCESS");
  const help = (code: string) => helpKey[code] ?? "CFG1_R1_CLOSE_HELP";
  const changed = (role: ConfiguredRole | null, chosen: Set<string>, newName: string, newDescription: string) =>
    role !== null &&
    (role.roleId === 0 ||
      !sameSet(chosen, role.permissions) ||
      (role.ownDefinition && (newName !== role.name || newDescription !== (role.description ?? ""))));
  const reset = (role: ConfiguredRole) => {
    setSelected(new Set(role.permissions));
    setName(role.name);
    setDescription(role.description ?? "");
    setNameError(false);
    setDirty(false);
  };
  const createFrom = (source: ConfiguredRole) => {
    setClone(source.roleId);
    setCurrent({
      ...source,
      roleId: 0,
      code: "",
      name: "",
      protected: false,
      ownDefinition: true,
      version: 0,
      memberCount: 0,
    });
    setSelected(new Set(source.permissions));
    setName("");
    setDescription(source.description ?? "");
    setDirty(true);
    setSection("actions");
  };
  const load = useCallback(
    () =>
      run(async () => {
        const reply = await api.get<ConfiguredRole[]>("roles/configuration");
        if (!accept(reply)) return;
        const all = reply.value ?? [];
        setRoles(all);
        setLoaded(true);
        const found = all.find((role) => role.roleId === roleId && role.operational) ?? null;
        setCurrent(found);
        if (found) {
          reset(found);
          if (duplicate) {
            createFrom(found);
            setViewOnly(false);
          }
        }
      }),
    [api, run, accept, roleId, duplicate],
  );
  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (code: string, on: boolean) => {
    if (readOnly) return;
    const next = new Set(selected);
    if (on) next.add(code);
    else next.delete(code);
    setSelected(next);
    setDirty(changed(current, next, name, description));
  };
  const changeLines = current
    ? [...actionCodes, readGroup]
        .filter((code) => current.permissions.includes(code) !== selected.has(code))
        .map((code) =>
          formatText(T(selected.has(code) ? "CFG1_R1_CHANGE_ON" : "CFG1_R1_CHANGE_OFF"), permissionName(code)),
        )
    : [];
  /** Si el cambio de permisos alcanza a miembros, C12 con el número y la lista; si no, se guarda. */
  const requestSave = async () => {
    if (!current || !dirty) return;
    if ((current.roleId === 0 || current.ownDefinition) && name.trim().length === 0) {
      setNameError(true);
      return;
    }
    if (current.roleId > 0 && current.memberCount > 0 && !sameSet(selected, current.permissions)) {
      confirmWindow.current?.open();
      return;
    }
    await save();
  };
  const save = async () => {
    const role = current!;
    let saved: ConfiguredRole | null = null;
    const created = role.roleId === 0;
    await run(async () => {
      const reply = await api.post<ConfiguredRole>("roles/configuration", {
        roleId: role.roleId,
        cloneRoleId: clone,
        name: role.ownDefinition || created ? name.trim() : role.name,
        description: role.ownDefinition || created ? description : role.description,
        permissions: [...selected],
        expectedVersion: role.version,
      });
      if (accept(reply)) saved = reply.value;
    });
    confirmWindow.current?.close();
    if (!saved) return;
    const result: ConfiguredRole = saved;
    setDirty(false);
    setClone(null);
    feedback.show(formatText(T(created ? "CFG1_R1_ROLE_CREATED" : "CFG1_R1_ROLE_SAVED"), roleName(result)));
    // La función que devolvió el servidor ya es lo guardado; la nueva queda en la dirección sin navegar.
    setRoles(
      roles.some((item) => item.roleId === result.roleId)
        ? roles.map((item) => (item.roleId === result.roleId ? result : item))
        : [...roles, result],
    );
    setCurrent(result);
    reset(result);
    if (created) {
      setCreatedId(result.roleId);
      replaceUrl(`/portal/chat/configuracion/roles/${result.roleId}${setup.listQuery}`);
    }
  };

  let body: React.ReactNode;
  if (current !== null) {
    const role = current;
    body = (
      <>
        <header className="tm-head tm-agent-head" data-part="encabezado">
          <div className="tm-head-title">
            <a
              className="tm-back"
              href={portalHref(listHref)}
              aria-label={T("CFG1_ROLES")}
              title={T("CFG1_ROLES")}
              data-part="atras"
            >
              <ChatIcon name="arrow-left" />
            </a>
            <div>
              <h1>{role.roleId === 0 ? T("CFG1_C_NEW_GROUP_FUNCTION") : roleName(role)}</h1>
              {role.roleId > 0 ? (
                <p className="tm-head-sub tm-head-meta">
                  {roleDescription(role)} · {membersText(ui, role.memberCount)}
                </p>
              ) : null}
            </div>
          </div>
          {viewOnly && canEdit(role) ? (
            <div className="tm-head-actions">
              <a className="tm-btn secondary" href={portalHref(editHref)}>
                {T("CFG1_EDIT")}
              </a>
            </div>
          ) : null}
        </header>
        {role.protected || role.actorUsesRole ? (
          <div className="tm-notice" role="status" data-part="sin-autoridad">
            <ChatIcon name="info" />
            <span>{T(role.protected ? "CFG1_PROTECTED_ROLE" : "CFG1_SELF_ROLE")}</span>
          </div>
        ) : null}
        {error !== null ? (
          <div className="tm-notice error" role="alert">
            <ChatIcon name="circle-alert" />
            <span>{error}</span>
          </div>
        ) : null}
        <div className="tm-agent" data-part="funcion-grupo">
          {!readOnly && (role.roleId === 0 || role.ownDefinition) ? (
            <div className="tm-fields">
              <div className={`tm-field ${nameError ? "invalid" : ""}`}>
                <label htmlFor="tm-role-name">{T("CFG1_NAME")}</label>
                <input
                  id="tm-role-name"
                  value={name}
                  maxLength={100}
                  onChange={(event) => {
                    setName(event.target.value);
                    setNameError(false);
                    setDirty(changed(current, selected, event.target.value, description));
                  }}
                />
                {nameError ? <span className="tm-field-error">{T("CFG1_R1_NAME_REQUIRED")}</span> : null}
              </div>
              <div className="tm-field">
                <label htmlFor="tm-role-description">
                  {T("CFG1_DESCRIPTION")} <span className="tm-hint">{T("CFG1_R1_OPTIONAL")}</span>
                </label>
                <input
                  id="tm-role-description"
                  value={description}
                  maxLength={2000}
                  onChange={(event) => {
                    setDescription(event.target.value);
                    setDirty(changed(current, selected, name, event.target.value));
                  }}
                />
              </div>
            </div>
          ) : null}
          <div className="tm-pills tm-sections" role="tablist" aria-label={roleName(role)} data-part="subpestanas">
            {(
              [
                ["scope", "CFG1_SCOPE"],
                ["actions", "CFG1_ACTIONS"],
                ["menus", "CFG1_MENUS"],
              ] as const
            ).map(([key, label]) => (
              <button
                key={key}
                type="button"
                role="tab"
                className="tm-pill"
                aria-selected={section === key ? "true" : "false"}
                onClick={() => setSection(key)}
              >
                {T(label)}
              </button>
            ))}
          </div>
          {section === "actions" ? (
            <>
              <section className="tm-section" aria-labelledby="tm-role-actions">
                <h2 id="tm-role-actions">{T("CFG1_CUSTOMER_CONVERSATIONS")}</h2>
                {actionCodes.map((code) => (
                  <TeamSwitch
                    key={code}
                    title={permissionName(code)}
                    line={T(help(code) + (selected.has(code) ? "" : "_OFF"))}
                    checked={selected.has(code)}
                    readOnly={readOnly}
                    onChange={(value) => toggle(code, value)}
                  />
                ))}
              </section>
              <section className="tm-section" aria-labelledby="tm-role-contacts">
                <h2 id="tm-role-contacts">{T("CFG1_SHARED_CONTACTS_TITLE_BASE")}</h2>
                <p>{T("CFG1_SHARED_CONTACTS_HINT_BASE")}</p>
                <a className="tm-btn secondary" href={portalHref("/portal/configuracion/equipo")}>
                  {T("CFG1_VIEW_PERMISSIONS")}
                </a>
              </section>
            </>
          ) : section === "scope" ? (
            <>
              <section className="tm-section" aria-labelledby="tm-role-scope">
                <h2 id="tm-role-scope">{T("CFG1_CUSTOMER_CONVERSATIONS")}</h2>
                <p>{T("CFG1_SCOPE_HINT")}</p>
                <TeamSwitch
                  title={T("CFG1_GROUP_READ")}
                  line={T(selected.has(readGroup) ? "CFG1_R1_GROUP_READ_HELP" : "CFG1_R1_GROUP_READ_HELP_OFF")}
                  checked={selected.has(readGroup)}
                  readOnly={readOnly}
                  onChange={(value) => toggle(readGroup, value)}
                />
                <a className="tm-btn secondary" href={portalHref("/portal/bandejas")}>
                  {T("CFG1_INBOXES")}
                </a>
              </section>
              <section className="tm-section" aria-labelledby="tm-role-internal">
                <h2 id="tm-role-internal">{T("CFG1_INTERNAL_CONVERSATIONS")}</h2>
                <p>{T("CFG1_INTERNAL_SCOPE_HINT")}</p>
                {role.roleId > 0 ? (
                  <a className="tm-btn secondary" href={portalHref(`/portal/chats?config=groups&role=${role.roleId}`)}>
                    {T("CFG1_INTERNAL_POLICY")}
                  </a>
                ) : null}
              </section>
            </>
          ) : (
            <section className="tm-section" aria-label={T("CFG1_MENUS")}>
              <p>{T("CFG1_MENU_AUTHORITY_HINT")}</p>
              <a className="tm-btn secondary" href={portalHref("/portal/configuracion/equipo")}>
                {T("CFG1_ACCESS")}
              </a>
            </section>
          )}
        </div>
        {!readOnly ? (
          <div className="tm-agent-footer" data-fixed-actions="" data-part="guardar">
            <span className="tm-agent-count" aria-live="polite">
              {count(ui, role.memberCount, "CFG1_C_AFFECTS_ONE", "CFG1_C_AFFECTS_MANY")}
            </span>
            <button
              type="button"
              className="tm-btn secondary"
              onClick={() => {
                setDirty(false);
                navigate(listHref);
              }}
            >
              {T("CFG1_CANCEL")}
            </button>
            <button
              type="button"
              className="tm-btn primary"
              disabled={busy || !dirty}
              onClick={() => void requestSave()}
            >
              {T("CFG1_B_SAVE")}
            </button>
          </div>
        ) : null}
      </>
    );
  } else if (error !== null)
    body = (
      <div className="tm-notice error" role="alert">
        {error}{" "}
        <button type="button" className="tm-link" onClick={() => void load()}>
          {T("CFG1_RETRY")}
        </button>
      </div>
    );
  else if (loaded)
    body = (
      <TeamEmpty
        title={T("CFG1_R1_ROLE_NOT_FOUND")}
        line={T("CFG1_R1_ROLE_NOT_FOUND_LINE_BASE")}
        actionLabel={T("CFG1_ROLES")}
        actionHref={portalHref("/portal/chat/configuracion/roles")}
      />
    );
  else body = <More />;
  return (
    <>
      <DraftGuard dirty={dirty} />
      {body}
      <TeamWindow
        ref={confirmWindow}
        confirm
        title={formatText(T("CFG1_R1_SAVE_ROLE_QUESTION"), current === null ? "" : roleName(current))}
        closeLabel={T("CFG1_B_KEEP_EDITING")}
        footer={
          <>
            <button type="button" className="tm-btn secondary" onClick={() => confirmWindow.current?.close()}>
              {T("CFG1_B_KEEP_EDITING")}
            </button>
            <button type="button" className="tm-btn primary" disabled={busy} onClick={() => void save()}>
              {T("CFG1_B_SAVE")}
            </button>
          </>
        }
      >
        <p>{count(ui, current?.memberCount ?? 0, "CFG1_R1_CHANGES_FOR_ONE", "CFG1_R1_CHANGES_FOR_MANY")}</p>
        <ul className="tm-change-list">
          {changeLines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </TeamWindow>
    </>
  );
}
